import { EditablePriceScale } from '../../api/asset/assetApiTypes';

/**
 * The venue sub-day unit rows (#1713): `hour` is a row with
 * `durationInMinutes: 60`, `half_day` one with `720` — a MARKER the resolver
 * anchors at the category's half-day cutoff, never "any 12 hours". Both are
 * OPTIONAL rows on a venue sheet: absent means the unit is not offered, and
 * the resolver prices from the day rows alone (ADR-0018 — no fallback
 * pricing, absence of a row is absence of the unit).
 *
 * Same editing rules as the class rates (`classRateRows.ts`), for the same
 * invisible-bug reasons:
 * - **A blank field removes the row.** An absent row means "no hourly
 *   booking price"; a 0 row would make every hour free.
 * - **The full scope travels** — the save path matches on it and archives
 *   what it cannot match.
 */

export interface SubDayUnit {
    key: 'hour' | 'half_day';
    durationInMinutes: 60 | 720;
}

export const SUB_DAY_UNITS: readonly SubDayUnit[] = [
    { key: 'hour', durationInMinutes: 60 },
    { key: 'half_day', durationInMinutes: 720 },
];

/** Is this row one of the venue sub-day unit rows? */
export const isSubDayRow = (s: EditablePriceScale): boolean =>
    (s.durationInMinutes === 60 || s.durationInMinutes === 720)
    && s.isFullDay !== true;

/** The general (class-less) sub-day row of one unit, if the tenant set one. */
export const subDayRowFor = (
    scales: EditablePriceScale[],
    unit: SubDayUnit,
): EditablePriceScale | undefined =>
    scales.find(
        (s) => !s.customerClassId
            && s.durationInMinutes === unit.durationInMinutes
            && s.isFullDay !== true,
    );

/**
 * The scales array after the operator typed `raw` into a sub-day price field.
 * `''` removes the row; an unparseable value leaves the array unchanged
 * (the general price fields' "ignore it" behaviour).
 */
export const applySubDayPrice = (
    scales: EditablePriceScale[],
    unit: SubDayUnit,
    raw: string,
): EditablePriceScale[] => {
    const existing = subDayRowFor(scales, unit);

    if (raw === '') {
        return existing ? scales.filter((s) => s !== existing) : scales;
    }

    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return scales;

    if (existing) {
        return scales.map((s) => (s === existing ? { ...s, price: parsed } : s));
    }

    // `id` deliberately absent — the save path creates unmatched rows.
    // `days: null`: the resolver matches these rows by their duration marker
    // and applies them on weekdays only by its own rule; a day scope here
    // would be a second spelling of that rule.
    return [
        ...scales,
        {
            price: parsed,
            days: null,
            durationInMinutes: unit.durationInMinutes,
            isFullDay: false,
            startTime: null,
            endTime: null,
            additionalDaysPrice: null,
            customerClassId: null,
        },
    ];
};
